import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser'; // Import EmailJS
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Responsive window width hook
function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    function handleResize() { setWidth(window.innerWidth); }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return width;
}

const CorporatePartnership = () => {
  const width = useWindowWidth();

  // breakpoints
  const isMobile = width < 700;
  const isTablet = width >= 700 && width < 1200;
  const isDesktop = width >= 1200;

  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    emailAddress: '',
    phoneNumber: '',
    partnershipInterest: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handlers
  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (!formData.companyName || !formData.contactName || !formData.emailAddress || !formData.phoneNumber) {
        alert("Please fill out all required fields.");
        return;
    }
    setIsSubmitting(true);

    const serviceID = 'service_n36e8hg';
    const templateID = 'template_i9jkfri';
    const publicKey = 'VAEdCMXuUKeCOw6gb';

    const emailBody = `
      New Corporate Partnership Inquiry
      ---------------------------------

      Company Details:
      - Company Name: ${formData.companyName}
      - Contact Person: ${formData.contactName}
      - Email: ${formData.emailAddress}
      - Phone: ${formData.phoneNumber || 'Not provided'}

      Partnership Interest & Requirements:
      ${formData.partnershipInterest}
    `;

    const templateParams = {
        email_subject: `Corporate Partnership Inquiry from ${formData.companyName}`,
        email_body: emailBody.trim()
    };

    emailjs.send(serviceID, templateID, templateParams, publicKey)
      .then((response) => {
        console.log('SUCCESS!', response.status, response.text);
        alert('Thank you for your partnership inquiry! Our team will get in touch with you shortly.');
        setIsSubmitting(false);
        setFormData({
            companyName: '', contactName: '', emailAddress: '', phoneNumber: '', partnershipInterest: ''
        });
      })
      .catch((err) => {
        console.error('FAILED...', err);
        alert('An error occurred while sending your inquiry. Please try again later.');
        setIsSubmitting(false);
      });
  };

  // --- Styles ---
  const styles = {
    fontFamily: {
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    },

    // Hero
    heroSection: {
      background: 'linear-gradient(135deg, #2d1b69 0%, #4a3ba1 50%, #6b5ce7 100%)',
      minHeight: isMobile ? '75vh' : '100vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center',
      color: 'white',
      position: 'relative',
      overflow: 'hidden',
      padding: isMobile ? '56px 10px 28px' : '0'
    },
    heroInner: {
      position: 'relative',
      zIndex: 2,
      maxWidth: isMobile ? '97vw' : '800px',
      padding: isMobile ? '0' : '0 20px'
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      backgroundColor: 'rgba(255,255,255,0.16)',
      padding: isMobile ? '7px 13px' : '8px 16px',
      borderRadius: '20px',
      marginBottom: isMobile ? '20px' : '40px',
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: '500'
    },
    badgeDot: {
      width: '8px',
      height: '8px',
      backgroundColor: 'white',
      borderRadius: '50%',
      marginRight: '8px'
    },
    heroTitle: {
      fontSize: isMobile ? '2rem' : '64px',
      fontWeight: 700,
      margin: isMobile ? '0 0 18px 0' : '0 0 20px 0',
      lineHeight: '1.1'
    },
    heroDesc: {
      fontSize: isMobile ? '1rem' : '18px',
      color: 'rgba(255, 255, 255, 0.9)',
      maxWidth: isMobile ? '97vw' : '600px',
      margin: isMobile ? '0 auto 24px' : '0 auto 40px',
      marginBottom: isMobile ? '24px' : '40px'
    },
    heroButtons: {
      display: 'flex',
      gap: isMobile ? '10px' : '16px',
      justifyContent: 'center',
      flexWrap: 'wrap'
    },
    heroBtn: {
      backgroundColor: 'rgba(255,255,255,0.22)',
      color: 'white',
      border: '1px solid rgba(255,255,255,0.32)',
      padding: isMobile ? '9px 15px' : '12px 24px',
      borderRadius: '8px',
      fontSize: isMobile ? '15px' : '16px',
      fontWeight: '500',
      cursor: 'pointer',
      textDecoration: 'none',
      transition: 'all 0.3s'
    },

    // Partnership Model
    section: {
      backgroundColor: '#f8f9fa',
      padding: isMobile ? '38px 6px' : '100px 20px',
      textAlign: 'center'
    },
    sectionInner: {
      maxWidth: isMobile ? '97vw' : '1200px',
      margin: '0 auto'
    },
    sectionTitle: {
      fontSize: isMobile ? '1.45rem' : '48px',
      fontWeight: 700,
      color: '#2d1b69',
      marginBottom: isMobile ? '9px' : '16px'
    },
    sectionSub: {
      fontSize: isMobile ? '0.99rem' : '18px',
      color: '#666',
      marginBottom: isMobile ? '24px' : '80px',
      maxWidth: isMobile ? '97vw' : '600px',
      margin: '0 auto'
    },
    stepsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : (isTablet ? '1fr 1fr' : 'repeat(auto-fit, minmax(350px, 1fr))'),
      gap: isMobile ? '22px' : '60px',
      marginBottom: isMobile ? '28px' : '100px'
    },
    step: { textAlign: 'left' },
    stepCircle: {
      width: '52px',
      height: '52px',
      backgroundColor: '#2d1b69',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: isMobile ? '19px' : '24px',
      fontWeight: 700,
      marginBottom: isMobile ? '14px' : '24px'
    },
    stepHeading: {
      fontSize: isMobile ? '1.09rem' : '24px',
      fontWeight: 700,
      color: '#2d1b69',
      marginBottom: isMobile ? '7px' : '16px',
      lineHeight: 1.23
    },
    stepDesc: {
      fontSize: isMobile ? '0.97rem' : '16px',
      color: '#666',
      marginBottom: isMobile ? '14px' : '24px',
      lineHeight: 1.6
    },
    stepList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px'
    },
    stepListItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    checkCircle: {
      width: '20px',
      height: '20px',
      border: '2px solid #2d1b69',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    checkSmall: {
      width: '8px',
      height: '8px',
      backgroundColor: '#2d1b69',
      borderRadius: '50%'
    },
    stepText: {
      fontSize: isMobile ? '0.99rem' : '16px',
      color: '#333'
    },

    // Advantages
    advSectionTitle: {
      fontSize: isMobile ? '1.45rem' : '48px',
      fontWeight: 700,
      color: '#2d1b69',
      marginBottom: isMobile ? '9px' : '16px'
    },
    advSub: {
      fontSize: isMobile ? '0.99rem' : '18px',
      color: '#666',
      marginBottom: isMobile ? '24px' : '80px',
      maxWidth: isMobile ? '97vw' : '600px',
      margin: '0 auto'
    },
    advGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : (isTablet ? '1fr 1fr' : 'repeat(auto-fit, minmax(350px, 1fr))'),
      gap: isMobile ? '21px' : '60px'
    },
    advCard: { textAlign: 'center' },
    advCircle: {
      width: isMobile ? '51px' : '80px',
      height: isMobile ? '51px' : '80px',
      backgroundColor: '#2d1b69',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 18px auto'
    },
    advIconBg: {
      width: isMobile ? '24px' : '40px',
      height: isMobile ? '24px' : '40px',
      backgroundColor: 'white',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: isMobile ? '1rem' : '20px',
      color: '#2d1b69'
    },
    advHeading: {
      fontSize: isMobile ? '1.12rem' : '24px',
      fontWeight: 700,
      color: '#2d1b69',
      marginBottom: '12px'
    },
    advDesc: {
      fontSize: isMobile ? '0.97rem' : '16px',
      color: '#666',
      lineHeight: '1.6'
    },

    // Contact & Form Section
    contactSection: {
      backgroundColor: 'white',
      padding: isMobile ? '32px 2vw' : '100px 20px'
    },
    contactLayout: {
      maxWidth: isMobile ? '97vw' : '1200px',
      margin: '0 auto',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '32px' : '80px',
      alignItems: isMobile ? undefined : 'start'
    },
    contactLeft: { flex: 1 },
    contactTitle: {
      fontSize: isMobile ? '1.23rem' : '48px',
      fontWeight: 700,
      color: '#2d1b69',
      marginBottom: '18px',
      lineHeight: '1.1'
    },
    contactDesc: {
      fontSize: isMobile ? '1rem' : '18px',
      color: '#666',
      marginBottom: isMobile ? '32px' : '60px',
      lineHeight: '1.6'
    },
    contactList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '24px'
    },
    contactListItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px'
    },
    contactIcon: {
      width: isMobile ? '34px' : '48px',
      height: isMobile ? '34px' : '48px',
      backgroundColor: '#2d1b69',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: isMobile ? '1rem' : '20px'
    },
    contactItemTitle: {
      fontSize: isMobile ? '0.98rem' : '16px',
      fontWeight: 600,
      color: '#2d1b69'
    },
    contactItemVal: {
      fontSize: isMobile ? '0.93rem' : '16px',
      color: '#666'
    },

    // Form
    formCard: {
      flex: 1,
      backgroundColor: '#f8f9fa',
      padding: isMobile ? '18px 7px' : '40px',
      borderRadius: '12px'
    },
    formTitle: {
      fontSize: isMobile ? '1.15rem' : '24px',
      fontWeight: 700,
      color: '#2d1b69',
      marginBottom: isMobile ? '13px' : '32px'
    },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
      gap: '20px',
      marginBottom: isMobile ? '13px' : '20px'
    },
    formLabel: {
      display: 'block',
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '7px'
    },
    input: {
      width: '100%',
      padding: isMobile ? '10px' : '12px 16px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      fontSize: isMobile ? '0.97rem' : '16px',
      backgroundColor: 'white',
      boxSizing: 'border-box'
    },
    textArea: {
      width: '100%',
      padding: isMobile ? '10px' : '12px 16px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      fontSize: isMobile ? '0.97rem' : '16px',
      backgroundColor: 'white',
      resize: 'vertical',
      fontFamily: 'inherit',
      boxSizing: 'border-box'
    },
    formBtn: {
      width: '100%',
      backgroundColor: '#2d1b69',
      color: 'white',
      border: 'none',
      padding: isMobile ? '13px' : '16px 24px',
      borderRadius: '8px',
      fontSize: isMobile ? '1rem' : '16px',
      fontWeight: 600,
      cursor: 'pointer',
      marginTop: isMobile ? '10px' : '0',
      transition: 'background-color 0.3s'
    }
  };

  // --- Render
  return (
    <div style={styles.fontFamily}>
      <Navbar />
      {/* HERO */}
      <div style={styles.heroSection}>
        {/* Decorative backgrounds */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none',
          backgroundImage: `
            radial-gradient(circle at 20% 80%, rgba(120,119,198,0.26) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255,255,255,0.12) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(120,119,198,0.10) 0%, transparent 50%)
          `,
          zIndex: 1
        }} />
        <div style={{
          position: 'absolute', bottom: isMobile ? '2%' : '10%', right: isMobile ? '2%' : '10%',
          width: isMobile ? '75px' : '200px', height: isMobile ? '75px' : '200px',
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.23) 2px, transparent 2px)',
          backgroundSize: '20px 20px', zIndex: 1
        }} />

        <div style={styles.heroInner}>
          <div style={styles.badge}>
            <div style={styles.badgeDot} />
            Global AI Society
          </div>
          <h1 style={styles.heroTitle}>
            Corporate<br />Partnership
          </h1>
          <p style={styles.heroDesc}>
            Empowering the future with AI Education. Join our global community of learners, researchers, and innovators shaping the ethical future of artificial intelligence.
          </p>
          <div style={styles.heroButtons}>
            <a href="#contact-form" style={styles.heroBtn}>Explore Partnership</a>
          </div>
        </div>
      </div>

      {/* PARTNERSHIP MODEL */}
      <div style={styles.section}>
        <div style={styles.sectionInner}>
          <h2 style={styles.sectionTitle}>Our Partnership Model</h2>
          <p style={styles.sectionSub}>
            A collaborative approach that bridges the gap between academic excellence and industry innovation
          </p>
          <div style={{...styles.stepsGrid, margin: isMobile ? '5em 1em' : '4em 0 9em' }}>
            {/* Step 1 */}
            <div style={styles.step}>
              <div style={styles.stepCircle}>1</div>
              <div style={styles.stepHeading}>Student Training &<br />Development</div>
              <div style={styles.stepDesc}>
                We provide comprehensive AI education through our partner institutes, developing students with practical skills and industry knowledge.
              </div>
              <div style={styles.stepList}>
                {[
                  'Hands-on project experience',
                  'Industry-relevant curriculum',
                  'Mentorship programs',
                  'Certification pathways'
                ].map((item, idx) => (
                  <div key={idx} style={styles.stepListItem}>
                    <div style={styles.checkCircle}><div style={styles.checkSmall} /></div>
                    <span style={styles.stepText}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Step 2 */}
            <div style={styles.step}>
              <div style={styles.stepCircle}>2</div>
              <div style={styles.stepHeading}>Your Investment & Returns</div>
              <div style={styles.stepDesc}>
                Partner companies invest in education and receive valuable returns through access to top talent.
              </div>
              <div style={styles.stepList}>
                {[
                  'Priority hiring access',
                  'Research collaboration',
                  'Industry visit programs',
                  'Custom project development'
                ].map((item, idx) => (
                  <div key={idx} style={styles.stepListItem}>
                    <div style={styles.checkCircle}><div style={styles.checkSmall} /></div>
                    <span style={styles.stepText}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Step 3 */}
            <div style={styles.step}>
              <div style={styles.stepCircle}>3</div>
              <div style={styles.stepHeading}>Mutual Growth</div>
              <div style={styles.stepDesc}>
                Together, we create an ecosystem that benefits students, institutions, and industry partners.
              </div>
              <div style={styles.stepList}>
                {[
                  'Skill-industry alignment',
                  'Innovation pipeline',
                  'Knowledge exchange',
                  'Economic development'
                ].map((item, idx) => (
                  <div key={idx} style={styles.stepListItem}>
                    <div style={styles.checkCircle}><div style={styles.checkSmall} /></div>
                    <span style={styles.stepText}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Advantages */}
          <h2 style={styles.advSectionTitle}>Partnership Advantages</h2>
          <p style={{...styles.advSub, padding: '0 0 4em'}}>
            Discover the strategic benefits of joining our global AI education ecosystem
          </p>
          <div style={styles.advGrid}>
            {[
              { icon: '👥', title: 'Skilled Talent Pipeline', desc: 'Access to a continuous stream of AI-trained professionals ready to contribute to your organization' },
              { icon: '📚', title: 'Customized Training Programs', desc: 'Tailored curriculum development aligned with your industry-specific AI needs and requirements' },
              { icon: '🤝', title: 'Industry Collaboration', desc: 'Joint research projects, case studies, and real-world problem-solving initiatives' },
              { icon: '🚀', title: 'Innovation Acceleration', desc: 'Early access to cutting-edge AI research and emerging technologies through our academic network' },
              { icon: '🌐', title: 'Global Network Access', desc: 'Connect with AI professionals and researchers from our worldwide community' },
              { icon: '💡', title: 'Thought Leadership', desc: 'Position your company as an AI innovation leader through joint publications and events' }
            ].map(({ icon, title, desc }, idx) => (
              <div key={idx} style={styles.advCard}>
                <div style={styles.advCircle}>
                  <div style={styles.advIconBg}>{icon}</div>
                </div>
                <div style={styles.advHeading}>{title}</div>
                <div style={styles.advDesc}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CONTACT SECTION */}
      <div id="contact-form" style={styles.contactSection}>
        <div style={styles.contactLayout}>
          {/* Info */}
          <div style={styles.contactLeft}>
            <h2 style={styles.contactTitle}>Start Your Partnership Journey</h2>
            <div style={styles.contactDesc}>
              Ready to transform education and accelerate innovation? Let's discuss how we can create a mutually beneficial partnership that drives growth and success.
            </div>
            <div style={styles.contactList}>
              <div style={styles.contactListItem}>
                <div style={styles.contactIcon}>📞</div>
                <div>
                  <div style={styles.contactItemTitle}>Phone</div>
                  <div style={styles.contactItemVal}>+91 99000 01511</div>
                </div>
              </div>
              <div style={styles.contactListItem}>
                <div style={styles.contactIcon}>✉️</div>
                <div>
                  <div style={styles.contactItemTitle}>Email</div>
                  <div style={styles.contactItemVal}>info@globalaisociety.com</div>
                </div>
              </div>
              <div style={styles.contactListItem}>
                <div style={styles.contactIcon}>📍</div>
                <div>
                  <div style={styles.contactItemTitle}>Address</div>
                  <div style={styles.contactItemVal}>
                    Global AI Society Headquarters<br />
                    Bengaluru, Karnataka, India
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Form */}
          <div style={styles.formCard}>
            <div style={styles.formTitle}>Partnership Inquiry</div>
            <form onSubmit={handleSubmit}>
              {/* First Row */}
              <div style={styles.formGrid}>
                <div>
                  <label style={styles.formLabel}>Company Name</label>
                  <input
                    type="text" name="companyName" value={formData.companyName}
                    onChange={handleInputChange}
                    style={styles.input}
                    required
                  />
                </div>
                <div>
                  <label style={styles.formLabel}>Contact Name</label>
                  <input
                    type="text" name="contactName" value={formData.contactName}
                    onChange={handleInputChange}
                    style={styles.input}
                    required
                  />
                </div>
              </div>
              {/* Second Row */}
              <div style={styles.formGrid}>
                <div>
                  <label style={styles.formLabel}>Email Address</label>
                  <input
                    type="email" name="emailAddress" value={formData.emailAddress}
                    onChange={handleInputChange}
                    style={styles.input}
                    required
                  />
                </div>
                <div>
                  <label style={styles.formLabel}>Phone Number</label>
                  <input
                    type="tel" name="phoneNumber" value={formData.phoneNumber}
                    onChange={handleInputChange}
                    style={styles.input}
                    required
                  />
                </div>
              </div>
              {/* Textarea */}
              <div style={{ marginBottom: isMobile ? '18px' : '32px' }}>
                <label style={styles.formLabel}>Partnership Interest & Requirements</label>
                <textarea
                  name="partnershipInterest"
                  value={formData.partnershipInterest}
                  onChange={handleInputChange}
                  placeholder="Tell us about your organization, partnership goals, and specific requirements..."
                  rows={isMobile ? 4 : 6}
                  style={styles.textArea}
                />
              </div>
              <button type="submit" style={styles.formBtn} disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit Partnership Inquiry →'}
              </button>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CorporatePartnership;
